/**
 * Austrian license-plate display formatting.
 *
 * Plates are STORED raw — no separators, upper-case — and the API returns
 * them that way (`T76672`, `S9988BB`). For display we render the
 * conventional district dash + group space (`T-76672`, `S-9988 BB`) so
 * they read the way they look on a real plate. A Java twin of this exact
 * rule lives server-side; keep the two in lock-step.
 *
 * A standard AT plate is a 1–2 letter district code, then a digit block,
 * then an OPTIONAL trailing letter block (`T-76672` has none, `S-9988 BB`
 * has one). Everything else is returned unchanged (beyond upper-casing):
 *   - already-formatted plates (`W-9981 GH`) or ones with a hand-typed
 *     separator (`WN-MAX 1`) — contain a `-` or a space, skip
 *   - letter-first custom plates (`WMAX1`, `ABC123`) — the district
 *     boundary is unknowable without the registry, so we never guess
 *     (would mis-split)
 *   - foreign / non-standard plates — left as-is
 *
 * This keeps plates effectively opaque: we add readability where the
 * format is unambiguous and otherwise show exactly what the API gave us,
 * upper-cased for consistency (a plate has no meaningful lower case).
 */
const RAW_AT_PLATE = /^([A-Z]{1,2})(\d+)([A-Z]*)$/

/**
 * A plate is nullable end-to-end since backend V0207 — imported
 * Easy-Werkstatt vehicles and unregistered workshop stock genuinely have
 * none. Absent returns the empty string so callers can test truthiness
 * without a null check; `Plate` renders the placeholder.
 */
export function formatPlate(plate: string | null | undefined): string {
  if (plate == null) return ''
  const s = plate.trim().toUpperCase()
  if (s === '') return ''
  // Already has a separator — either our own formatting round-tripping
  // through here, or a human-typed one on a custom plate. Never re-split.
  if (s.includes('-') || s.includes(' ')) return s
  const m = RAW_AT_PLATE.exec(s)
  if (!m) return s
  const [, district, digits, letters] = m
  return letters ? `${district}-${digits} ${letters}` : `${district}-${digits}`
}

/**
 * Strip the conventional separators and upper-case, giving the raw form
 * plates are STORED in. `W-9981 GH` → `W9981GH`.
 */
export function compactPlate(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase()
}

/**
 * Live-typing formatter for a plate `<input>` — inserts the same
 * separators as {@link formatPlate}, but incrementally, as each
 * character lands, rather than only once a full raw plate is known.
 *
 * Two insertions, applied in order:
 *  1. No `-` yet, and the value starts with a 1–2 letter district code
 *     immediately followed by a digit → insert `-` right after the
 *     letters (`T2` → `T-2`).
 *  2. The value now reads `<district>-<digits><letters>` with no space
 *     before the trailing letters → insert one (`W-15979R` → `W-15979 R`).
 *
 * A hand-typed separator is left alone (rule 1 only fires when there is
 * NO `-` anywhere yet), so a custom plate like `WN-MAX 1` keeps exactly
 * the separator the human typed. Backspacing the auto-inserted `-` when
 * there is no digit behind it (`W-` → `W`) does not resurrect it, because
 * rule 1 requires a digit right after the letters to fire at all.
 *
 * This only ever INSERTS characters, never removes or reorders any of
 * what was typed — `usePlateInput` is what keeps the caret from jumping
 * to the end when that insertion lands ahead of it.
 */
export function formatPlateInput(value: string): string {
  let s = value.toUpperCase()
  if (!s.includes('-')) {
    const m = /^([A-Z]{1,2})(\d.*)$/.exec(s)
    if (m) s = `${m[1]}-${m[2]}`
  }
  const m2 = /^([A-Z]{1,2})-(\d+)([A-Z]+)$/.exec(s)
  if (m2) s = `${m2[1]}-${m2[2]} ${m2[3]}`
  return s
}

/**
 * Does this look like an Austrian plate? For form validation — the one
 * rule every app checks a typed plate against, hoisted from apps/web
 * (2026-09-28) so the booking form and the shop's own inputs cannot
 * disagree about what a plate is.
 *
 * A 1–2 letter district, an optional `-` or space, a 1–6 character
 * alphanumeric block (digits and/or letters — `G-12345`, `W-12ABC`,
 * `WT-7X`), then optionally a group space and a 1–4 letter block
 * (`S-9988 BB`). Case-insensitive; surrounding whitespace ignored. Every
 * plate `formatPlate` / `formatPlateInput` can produce from a valid one
 * passes, and so does the compact stored form (`W15979R`).
 *
 * Empty is VALID: the plate is optional on the forms that use this, and
 * the ones that require it say so themselves. "No plate" and "a bad
 * plate" are different errors.
 */
const PLATE_SHAPE = /^[A-Z]{1,2}[\s-]?[A-Z0-9]{1,6}(?: [A-Z]{1,4})?$/

export function isValidPlate(plate: string | null | undefined): boolean {
  if (plate == null) return true
  const s = plate.trim().toUpperCase()
  if (s === '') return true
  return PLATE_SHAPE.test(s)
}
